import React, { memo, useCallback, useMemo } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import PriceContent from 'Pods/SearchPage/Shared/PriceContent/PriceContent'
import { priceRangeOptions } from 'Pods/SearchPage/Shared/PriceContent/RangeButtons/constants'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { clearPricing } from 'Pods/SearchPage/Store/actions/pricing'
import { DEFAULT_STATE, SUBSCRIPTION_TIER } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

// Plan names stay untranslated, as in the SubscriptionTier radio group.
const SUBSCRIPTION_TIER_LABELS = {
  [SUBSCRIPTION_TIER.ESSENTIALS]: 'Essentials',
  [SUBSCRIPTION_TIER.PREMIUM]: 'Premium',
}

const isPriceBoundSet = (value, defaultValue) => value !== defaultValue && value !== 0

const formatPrice = value => `$${Number(value).toLocaleString('en-US')}`

// Figma 2989:109512: "Min $170", "Max $1,000", and the ranges' short "$50-100" shape for both.
const formatCustomPriceRange = (minPrice, maxPrice, translate) => {
  const hasMin = isPriceBoundSet(minPrice, DEFAULT_STATE.minPrice)
  const hasMax = isPriceBoundSet(maxPrice, DEFAULT_STATE.maxPrice)

  if (hasMin && hasMax) return `${formatPrice(minPrice)}-${Number(maxPrice).toLocaleString('en-US')}`
  if (hasMin) return translate('search_page.filters.price.short.min', { price: formatPrice(minPrice) })
  if (hasMax) return translate('search_page.filters.price.short.max', { price: formatPrice(maxPrice) })

  return null
}

function Price() {
  const translate = useTranslate()

  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const subscriptionTiers = useSearchPageStore(state => state.subscriptionTiers)
  const priceRanges = useSearchPageStore(state => state.priceRanges)
  const minPrice = useSearchPageStore(state => state.minPrice)
  const maxPrice = useSearchPageStore(state => state.maxPrice)

  const selectedValues = useMemo(() => {
    const values = []

    if (withSubscription) {
      // Premium holds every Essentials item, so it names the whole pick when both are ticked.
      const tier = [SUBSCRIPTION_TIER.PREMIUM, SUBSCRIPTION_TIER.ESSENTIALS]
        .find(candidate => subscriptionTiers?.includes(candidate))
      values.push(SUBSCRIPTION_TIER_LABELS[tier] ?? translate('search_page.filters.price.with_subscription'))
    }
    if (onSale) {
      values.push(translate('search_page.filters.price.short.on_sale'))
    }
    priceRangeOptions
      .filter(option => priceRanges?.includes(option.value))
      .forEach(option => values.push(translate(`search_page.filters.price.ranges_short.${option.labelKey}`)))

    const customPriceRange = formatCustomPriceRange(minPrice, maxPrice, translate)
    if (customPriceRange) {
      values.push(customPriceRange)
    }

    return values
  }, [onSale, withSubscription, subscriptionTiers, priceRanges, minPrice, maxPrice, translate])

  const hasSelectedAnyFilter = selectedValues.length > 0

  const handleReset = useCallback(() => {
    clearPricing()
  }, [])

  const content = useMemo(() => (
    <>
      <PriceContent />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasSelectedAnyFilter}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </>
  ), [handleReset, hasSelectedAnyFilter, translate])

  return (
    <FiltersWrapper
      trailIconSize="small"
      placeholder={translate('search_page.filters.price.title')}
      content={content}
      hasSelectedAnyFilters={hasSelectedAnyFilter}
      selectedValues={selectedValues}
      positionHorizontal="left"
      closingDropdownClassName="filter-wrapper main-filters__fixed-dropdown"
    />
  )
}

export default memo(Price)
